/**
 * How the wizard's Next on revisit step 1 reaches the re-read (Release 4).
 *
 * The wizard's state machine (useWizardController) calls `useRevisitController()` ABOVE the providers it
 * renders, so it cannot read RevisitProvider's context, and neither takes a prop from the other. The
 * mounted RevisitProvider therefore registers its `start` here (from an effect, so only a provider that is
 * on the screen is reachable), and the controller's `onStart` calls the one registered last. Brand Setup
 * is a whole page: one wizard is mounted at a time, and a workspace switch unmounts the old one (it is
 * keyed by the workspace) before the new one registers.
 */

import type { RereadSources } from '../../../lib/api';

export interface RereadStartOptions {
  /** What the person changed about the sources in step 1; absent: the stored sources as they are. */
  sources?: RereadSources;
  /** 0-1 while a new document uploads. */
  onProgress?: (fraction: number) => void;
}

export interface RevisitStarter {
  /** Starts "Re-read my sources"; resolves once the wizard may move on, rejects to keep it on step 1. */
  start(options?: RereadStartOptions): Promise<void>;
}

const starters: RevisitStarter[] = [];

/** Registers a mounted wizard's re-read; the returned function takes it away again (on unmount). */
export function registerRevisitStarter(starter: RevisitStarter): () => void {
  starters.push(starter);
  return () => {
    const at = starters.lastIndexOf(starter);
    if (at >= 0) starters.splice(at, 1);
  };
}

/** Starts a re-read in the wizard on the screen. Rejects when none is mounted. */
export function startMountedReread(options?: RereadStartOptions): Promise<void> {
  const starter = starters[starters.length - 1];
  return starter ? starter.start(options) : Promise.reject(new Error('Brand Setup is not open.'));
}
